import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { InstanceConfig, loadInstances } from '../config/instances'

interface InstanceContextType {
  currentInstance: InstanceConfig | null
  instances: InstanceConfig[]
  loading: boolean
  setCurrentInstance: (instance: InstanceConfig) => void
  switchInstance: (instanceId: string) => void
  refreshInstances: () => Promise<void>
}

const InstanceContext = createContext<InstanceContextType | undefined>(undefined)

export function InstanceProvider({ children }: { children: React.ReactNode }) {
  const [currentInstance, setCurrentInstanceState] = useState<InstanceConfig | null>(null)
  const [instances, setInstances] = useState<InstanceConfig[]>([])
  const [loading, setLoading] = useState(true)

  const refreshInstances = useCallback(async () => {
    try {
      setLoading(true)
      // Try to load from current instance's API, or use default
      const apiBaseUrl = currentInstance?.apiUrl || import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const loadedInstances = await loadInstances(apiBaseUrl)
      
      if (loadedInstances.length > 0) {
        setInstances(loadedInstances)
        
        // If no current instance, set to first one
        if (!currentInstance && loadedInstances.length > 0) {
          const savedInstanceId = localStorage.getItem('currentInstanceId')
          const instance = savedInstanceId
            ? loadedInstances.find(i => i.id === savedInstanceId)
            : loadedInstances[0]
          
          if (instance) {
            setCurrentInstance(instance)
          }
        }
      } else {
        // Fallback to default instance
        const defaultInstance: InstanceConfig = {
          id: 'local',
          name: 'Local Development',
          apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000',
          status: 'active',
          organizationDomain: 'local.karyva.com',
          adminEmail: 'root@karyva.com',
          createdAt: new Date().toISOString(),
        }
        setInstances([defaultInstance])
        setCurrentInstanceState(defaultInstance)
      }
    } catch (error) {
      console.error('Error refreshing instances:', error)
      // Fallback to default
      const defaultInstance: InstanceConfig = {
        id: 'local',
        name: 'Local Development',
        apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000',
        status: 'active',
        organizationDomain: 'local.karyva.com',
        adminEmail: 'root@karyva.com',
        createdAt: new Date().toISOString(),
      }
      setInstances([defaultInstance])
      setCurrentInstanceState(defaultInstance)
    } finally {
      setLoading(false)
    }
  }, [currentInstance])

  useEffect(() => {
    refreshInstances()
  }, []) // Only run on mount

  const setCurrentInstance = (instance: InstanceConfig) => {
    setCurrentInstanceState(instance)
    localStorage.setItem('currentInstanceId', instance.id)
    
    // Update API client base URL
    // This will be handled by the API client when making requests
  }

  const switchInstance = (instanceId: string) => {
    const instance = instances.find(i => i.id === instanceId)
    if (instance) {
      setCurrentInstance(instance)
      // Refresh instances after switch
      refreshInstances()
    }
  }

  return (
    <InstanceContext.Provider
      value={{
        currentInstance,
        instances,
        loading,
        setCurrentInstance,
        switchInstance,
        refreshInstances,
      }}
    >
      {children}
    </InstanceContext.Provider>
  )
}

export function useInstance() {
  const context = useContext(InstanceContext)
  if (!context) {
    throw new Error('useInstance must be used within InstanceProvider')
  }
  return context
}

